import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../../../authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import useFetchWithMsal from '../../useFetchWithMsal';

const useViewInvoice = () => {
  const { fileName } = useParams();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [invoice, setInvoice] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [invoiceVersion, setInvoiceVersion] = useState('original');
  const [hasAttachments, setHasAttachments] = useState(false);
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const calculateOptimalScale = useCallback((isMobile) => {
    const container = document.querySelector('.pdf-container');
    if (container) {
      const containerWidth = container.clientWidth - (isMobile ? 16 : 40);
      const pageWidth = 595;
      const maxScale = isMobile ? 1.2 : 1.5;
      return Math.min(containerWidth / pageWidth, maxScale);
    }
    return isMobile ? 0.8 : 1.0;
  }, []);  // Initialize with the default version (original)
  useEffect(() => {
    setInvoiceVersion('original');
  }, []);

  // Check for attachments
  const checkForAttachments = async (invoiceId) => {
    try {
      const response = await execute('GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/with-attachments/${invoiceId}`
      );

      if (response && response.success && response.attachments) {
        setHasAttachments(response.attachments.length > 0);
        console.log(`Found ${response.attachments.length} attachments for invoice ${invoiceId}`);
      } else {
        setHasAttachments(false);
      }
    } catch (error) {
      console.log('Error checking attachments:', error);
      setHasAttachments(false);
    }
  };

  // Fetch invoice (only once, since we'll get both versions)
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const isInvoiceNumber = /^\d+$/.test(fileName);
        const isObjectId = /^[0-9a-fA-F]{24}$/.test(fileName);
        const endpoint = isObjectId
          ? `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/${fileName}`
          : `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/filename/${fileName}`
        const response = await execute('GET', endpoint);
        if (response && response.success) {
          // Create invoice object with both file versions
          const invoiceWithFiles = {
            ...response.invoice,
            files: response.files
          };

          setInvoice(invoiceWithFiles);

          // Check if has attachments (if available in response)
          if (response.hasAttachments !== undefined) {
            setHasAttachments(response.hasAttachments);
          } else {
            // Fallback: check attachments separately
            checkAttachments(response.invoice.id || fileName);
          }

          // Set initial file content based on the default version (original)
          console.log('Invoice files available:',
            'original:', !!response.files.original,
            'signed:', !!response.files.signed,
            'hasOriginalVersion:', response.invoice.hasOriginalVersion);

          const fileContent = response.files.original && response.invoice.hasOriginalVersion
            ? response.files.original
            : response.files.signed;

          setInvoiceFile(fileContent);
          setPageNumber(1);

          // Check for attachments
          await checkForAttachments(response.invoice._id);
          console.log('Invoice data loaded:', response.invoice.id, 'hasAttachments will be checked');
        } else {
          setError('לא נמצאה חשבונית');
        }
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          try {
            await instance.loginRedirect({
              scopes: protectedResources.apiScopes.scopes.read,
              redirectUri: window.location.href
            });
          } catch (authError) {
            setError('יש להתחבר למערכת כדי לצפות בחשבונית');
          }
        } else {
          setError(err.message || 'שגיאה בטעינת החשבונית');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
    // eslint-disable-next-line
  }, [fileName, instance, execute]);

  const checkAttachments = async (invoiceId) => {
    try {
      const response = await execute('GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/attachments/${invoiceId}`
      );
      if (response && response.success && response.attachments) {
        setHasAttachments(response.attachments.length > 0);
      }
    } catch (error) {
      console.log('Error checking attachments:', error);
      setHasAttachments(false);
    }
  };

  const handleLogin = async () => {
    try {
      await instance.loginRedirect({
        scopes: protectedResources.apiScopes.scopes.read,
        redirectUri: window.location.href
      });
    } catch (error) {
      setError('שגיאה בתהליך ההתחברות');
    }
  };

  const handleBackToSystem = () => {
    navigate('/PurchaseInvoice');
  };

  const handleViewAttachments = () => {
    if (invoice && invoice._id) {
      navigate(`/v-a/${invoice._id}`);
    }
  };

  const handleDownloadFile = (fileData, invoiceNumber) => {
    if (!fileData) return;
    if (fileData.startsWith('/') || fileData.startsWith('+') || fileData.match(/^[A-Za-z0-9+/]/) && !fileData.startsWith('data:')) {
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `חשבונית-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    }
    const link = document.createElement('a');
    link.href = fileData;
    link.download = `חשבונית-${invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }; const handleDownload = () => {
    if (!invoiceFile || !invoice) return;
    // Download the currently selected version (original or signed)
    const fileNameSuffix = invoiceVersion === 'signed' ? '_signed' : '_original';
    const displayName = `${invoice.booknum}${fileNameSuffix}`;
    handleDownloadFile(invoiceFile, displayName);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(false);
  };

  const onDocumentLoadError = () => {
    setPdfError(true);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const toggleFullscreen = (isMobile) => {
    setIsFullscreen(prev => {
      if (!prev) {
        setScale(1.5);
      } else {
        setScale(calculateOptimalScale(isMobile));
      }
      return !prev;
    });
  };  // Get the PDF source based on the current file content
  const getPdfSrc = () => {
    if (!invoiceFile) return null;
    if (invoiceFile.startsWith('data:application/pdf') || invoiceFile.startsWith('http')) {
      return invoiceFile;
    } else if (invoiceFile.match(/^[A-Za-z0-9+/]+={0,2}$/)) {
      return `data:application/pdf;base64,${invoiceFile}`;
    }
    return null;
  };  // Toggle between original and signed versions
  const toggleInvoiceVersion = () => {
    const newVersion = invoiceVersion === 'original' ? 'signed' : 'original';
    setInvoiceVersion(newVersion);

    // Update the file content based on the selected version
    if (invoice && invoice.files) {
      // If we're switching to original version but it doesn't exist, stay with signed
      if (newVersion === 'original' && !invoice.hasOriginalVersion) {
        console.warn('Original version not available, using signed version');
      }

      // Get the appropriate file content based on version
      let fileContent;
      if (newVersion === 'original' && invoice.files.original && invoice.hasOriginalVersion) {
        console.log('Switching to original version');
        fileContent = invoice.files.original;
      } else {
        console.log('Switching to signed version');
        fileContent = invoice.files.signed;
      }

      setInvoiceFile(fileContent);
      setPageNumber(1); // Reset to first page when switching versions
    }
  };

  return {
    invoice,
    invoiceFile,
    loading,
    error,
    numPages,
    pageNumber,
    invoiceVersion,
    hasAttachments,
    toggleInvoiceVersion,
    setPageNumber,
    scale,
    setScale,
    isFullscreen,
    setIsFullscreen,
    pdfError,
    handleLogin,
    handleBackToSystem,
    handleViewAttachments,
    handleDownload,
    handleZoomIn,
    handleZoomOut,
    toggleFullscreen,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    getPdfSrc,
    calculateOptimalScale,
  };
};

export default useViewInvoice;
